import { ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Place, ExpandLess, ExpandMore } from "@mui/icons-material";
import { SiteDropdown } from "./site-dropdown";
import { useSite } from "../model/use-site";
import type { Site } from "../../../types/Site";

export interface SiteSelectorProps {
    allowedSites: Site[];
    selectedSite: Site | null;
    setSelectedSite: (site: Site) => void;
}

/**
 * SiteSelector
 *
 * Renders a button to toggle the site dropdown and displays the SiteDropdown component.
 * Uses useSiteSelection to manage dropdown state. Shows an icon and label, and expands/collapses the dropdown.
 *
 * - The button displays a location icon, the label "Sites", and an expand/collapse icon.
 * - When clicked, it toggles the dropdown open/closed.
 * - The SiteDropdown component is shown or hidden based on the dropdown state.
 */
export const SiteSelector = ({
    allowedSites,
    selectedSite,
    setSelectedSite,
}: SiteSelectorProps) => {
    const { isDropdownOpen, toggleDropdown } = useSite({
        allowedSites,
        selectedSite,
        setSelectedSite,
    });

    const theme = useTheme();

    return (
        <>
            <ListItemButton
                onClick={toggleDropdown}
                data-testid="site-selector-toggle"
            >
                <ListItemIcon>
                    <Place sx={{ color: theme.palette.primary.dark }} />
                </ListItemIcon>
                <ListItemText
                    primary="Sites"
                    slotProps={{
                        primary: {
                            style: {
                                color: theme.palette.text.primary,
                                fontWeight: "bold",
                                fontSize: 20,
                                lineHeight: "20px",
                                whiteSpace: "nowrap",
                            },
                        },
                    }}
                />
                {isDropdownOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <SiteDropdown
                isOpen={isDropdownOpen}
                allowedSites={allowedSites}
                setSelectedSite={setSelectedSite}
            />
        </>
    );
};
